
"use client"

import { useState, useEffect, useRef } from "react"
import { Formik, Form } from "formik"
import * as Yup from "yup"
import { Card, Col, Row, Button } from "react-bootstrap"
import { useNavigate, useParams, Link } from "react-router-dom"
import { toast } from '../../common/Toast/toast';

import TextInput from "../../common/FormFields/TextInput.jsx"
import SelectInput from "../../common/FormFields/SelectInput.jsx"
import DatePickerInput from "../../common/FormFields/DatePickerInput.jsx"
import { createJob, updateJob, getJobById } from "../../../../services/JobsService.js"
import { useAuth } from "../../../../context/AuthContext"

const PostJob = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { user: authUser, hasPermission } = useAuth()

  const [initialValues, setInitialValues] = useState({
    title: "",
    company: "",
    location: "",
    salary: "",
    date: "",
    status: "",
  })

  const [isLoading, setIsLoading] = useState(false)
  const isEditMode = Boolean(id)
  const hasShownToast = useRef(false)

  // console.log("[PostJob] Component initialized:", { isEditMode, jobId: id, authUser: authUser?.id })

  const validationSchema = Yup.object({
    title: Yup.string().required("Job title is required"),
    company: Yup.string().required("Company name is required"),
    location: Yup.string().required("Location is required"),
    salary: Yup.string().required("Salary is required"),
    date: Yup.date().required("Interview date is required"),
    status: Yup.string().required("Status is required"),
  })

  useEffect(() => {
    // console.log("[PostJob] Checking permissions for user:", authUser?.id)
    if (!hasPermission("post-jobs", "lms")) {
      if (!hasShownToast.current) {
        // console.log("[PostJob] Permission denied for job creation")
        toast.error("You do not have permission to post jobs.")
        navigate("/")
        hasShownToast.current = true
      }
    }
  }, [hasPermission, navigate])

  const fetchJobData = async () => {
    try {
      // console.log("[PostJob] Fetching job data for edit mode, ID:", id)
      setIsLoading(true)
      const response = await getJobById(id)
      const jobData = response.data
      // console.log("[PostJob] Fetched job data:", jobData)

      const formatDateLocal = (dateString) => {
        const date = new Date(dateString)
        return date.toISOString().slice(0, 16)
      }

      setInitialValues({
        title: jobData.title || "",
        company: jobData.company || "",
        location: jobData.location || "",
        salary: jobData.salary || "",
        date: jobData.date ? formatDateLocal(jobData.date) : "",
        status: jobData.status || "Scheduled",
      })

      // console.log("[PostJob] Initial values set for edit mode:", initialValues)
    } catch (error) {
      console.error("[PostJob] Failed to fetch job data:", error)
      toast.error("Failed to fetch job data")
      navigate("/list-jobs")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (isEditMode) {
      // console.log("[PostJob] Edit mode detected, fetching job data")
      fetchJobData()
    }
  }, [isEditMode, id])

  if (isLoading) {
    // console.log("[PostJob] Rendering loading state")
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="spinner-border text-warning" role="status">
          <span className="sr-only">Loading...</span>
        </div>
        <h4 className="ms-3">Loading Job...</h4>
      </div>
    )
  }

  // console.log("[PostJob] Rendering form with initial values:", initialValues)

  return (
    <>      <div className="row mb-3">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <h4 className="card-title mb-0">{isEditMode ? "Edit Job" : "Post New Job"}</h4>
                <small className="text-muted">
                  {isEditMode ? "Update job information" : "Create a new job posting"}
                </small>
              </div>
              <Link to="/list-jobs">
                <Button variant="secondary">
                  <i className="fa fa-arrow-left me-1"></i>
                  Back to Jobs List
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          try {
            // console.log("[PostJob] Form submission started with values:", values)
            setSubmitting(true)

            const payload = {
              title: values.title,
              company: values.company,
              location: values.location,
              salary: values.salary,
              date: values.date,
              status: values.status,
              createdBy: isEditMode ? undefined : authUser?.name || "Admin",
              updatedBy: authUser?.name || "Admin",
            }

            // console.log("[PostJob] Prepared payload:", payload)

            if (isEditMode) {
              // console.log("[PostJob] Updating existing job with ID:", id)
              const result = await updateJob(id, payload)
              // console.log("[PostJob] Job updated successfully:", result)
              toast.success(result.message?.message || "Job updated successfully!")
            } else {
              // console.log("[PostJob] Creating new job")
              const result = await createJob(payload)
              // console.log("[PostJob] Job created successfully:", result)
              toast.success(result.message?.message || "Job posted successfully!")
              resetForm()
            }

            setTimeout(() => {
              // console.log("[PostJob] Navigating back to jobs list")
              navigate("/list-jobs")
            }, 1500)
          } catch (err) {
            console.error("[PostJob] Form submission error:", err)
            console.error("[PostJob] Error details:", {
              message: err.message,
              response: err.response?.data,
              status: err.response?.status,
            })
            toast.error(`Failed to ${isEditMode ? "update" : "post"} job: ${err.message}`)
          } finally {
            setSubmitting(false)
            // console.log("[PostJob] Form submission completed")
          }
        }}
      >
        {({ values, setFieldValue, isSubmitting, errors, touched }) => {
          {/* console.log("[PostJob] Form render - Current values:", values)
          console.log("[PostJob] Form render - Errors:", errors)
          console.log("[PostJob] Form render - Touched fields:", touched) */}

          return (
            <Form className="p-4">
              <Card className="p-4">
                <div className="mb-4 border p-4 rounded">
                  <Row className="mb-3">
                    <Col md={12}>
                      <h5 className="text-warning">{isEditMode ? "Edit Job Posting" : "Post New Job"}</h5>
                    </Col>
                  </Row>

                  {/* Basic Job Information */}
                  <Row className="mb-3">
                    <Col md={6}>
                      <TextInput name="title" label="Job Title *" placeholder="Enter job title" />
                      {touched.title && errors.title && <div className="text-danger mt-1">{errors.title}</div>}
                    </Col>
                    <Col md={6}>
                      <TextInput name="company" label="Company Name *" placeholder="Enter company name" />
                      {touched.company && errors.company && <div className="text-danger mt-1">{errors.company}</div>}
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <TextInput name="location" label="Location *" placeholder="Enter job location" />
                      {touched.location && errors.location && <div className="text-danger mt-1">{errors.location}</div>}
                    </Col>
                    <Col md={6}>
                      <TextInput name="salary" label="Salary *" placeholder="e.g., 10 LPA" />
                      {touched.salary && errors.salary && <div className="text-danger mt-1">{errors.salary}</div>}
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <label className="form-label fw-bold text-dark">Interview Date *</label>
                      <DatePickerInput
                        name="date"
                        showTimeSelect
                        dateFormat="Pp"
                        placeholderText="Select interview date"
                        minDate={new Date()}
                      />
                      {touched.date && errors.date && <div className="text-danger mt-1">{errors.date}</div>}
                    </Col>
                    <Col md={6}>
                      <label className="form-label fw-bold text-dark">Status *</label>
                      <SelectInput
                        className="mt-0"
                        name="status"
                        options={[
                          { label: "active", value: "active" },
                          { label: "inactive", value: "inactive" },
                          // { label: "Cancelled", value: "Cancelled" },
                        ]}
                      />
                    </Col>
                  </Row>
                </div>

                <Row className="mt-4">
                  <Col md={6} className="text-start">
                    <Link to="/list-jobs">
                      <Button variant="outline-secondary" size="lg">
                        <i className="fa fa-times me-2"></i>
                        Cancel
                      </Button>
                    </Link>
                  </Col>
                  <Col md={6} className="text-end">
                    <Button variant="warning" type="submit" disabled={isSubmitting} size="lg" className="px-4">
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Processing...
                        </>
                      ) : isEditMode ? (
                        <>
                          <i className="fa fa-save me-2"></i>
                          Update Job
                        </>
                      ) : (
                        <>
                          <i className="fa fa-plus me-2"></i>
                          Post Job
                        </>
                      )}
                    </Button>
                  </Col>
                </Row>
              </Card>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

export default PostJob
